import { type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Group,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useLocalStorage } from "@mantine/hooks";
import {
  IconApps,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconUsers,
  IconDisc,
  IconServer,
} from "@tabler/icons-react";
import { useAuth } from "../../auth/AuthContext.js";
import { Logo } from "../../components/Logo.js";
import { ThemeToggle } from "../../components/ThemeToggle.js";
import { UserMenu } from "../../components/UserMenu.js";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  /** Match route exactly (vs. prefix). */
  end?: boolean;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

/**
 * Admin nav. Stable surface — admin functionality grows slowly, so this
 * list is hardcoded unlike the customer nav which has commented-out
 * roadmap placeholders.
 */
const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        to: "/admin/customers",
        label: "Customers",
        icon: <IconUsers size={18} stroke={1.75} />,
      },
      {
        to: "/admin/apps",
        label: "Apps",
        icon: <IconApps size={18} stroke={1.75} />,
      },
      {
        to: "/admin/isos",
        label: "ISO Allowlist",
        icon: <IconDisc size={18} stroke={1.75} />,
      },
      {
        to: "/admin/assignments",
        label: "VM Assignments",
        icon: <IconServer size={18} stroke={1.75} />,
      },
    ],
  },
];

/**
 * Admin app shell. Mirrors the customer shell's collapsible-sidebar
 * pattern — see CustomerLayout.tsx for the full design notes. Kept as a
 * separate component rather than parameterized because the admin and
 * customer shells may diverge (different nav config, different footer
 * actions, eventually different global search) and we don't want a
 * leaky abstraction.
 */
export function AdminLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useMantineTheme();
  const [collapsed, setCollapsed] = useLocalStorage<boolean>({
    key: "proxvps-admin-nav-collapsed",
    defaultValue: false,
  });
  const [mobileOpen, { toggle: toggleMobile }] = useDisclosure();

  function activeLabel(): string {
    for (const section of NAV_SECTIONS) {
      for (const item of section.items) {
        if (item.end && location.pathname === item.to) return item.label;
        if (!item.end && location.pathname.startsWith(item.to)) return item.label;
      }
    }
    return "Admin";
  }

  async function onLogout() {
    await auth.logout();
    navigate("/admin/login", { replace: true });
  }

  function renderNavItem(item: NavItem) {
    const link = (
      <NavLink to={item.to} end={item.end} style={{ textDecoration: "none" }}>
        {({ isActive }) => (
          <Box
            data-active={isActive || undefined}
            className="proxvps-nav-item"
            px="sm"
            py={8}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              borderRadius: theme.radius.md,
              // See CustomerLayout.tsx for the color/contrast rationale —
              // explicit color is required because NavLink renders an
              // unstyled <a> that otherwise inherits browser-default
              // hyperlink blue. Hover lives in the .proxvps-nav-item class
              // in index.html.
              color: isActive
                ? "var(--mantine-primary-color-light-color)"
                : "var(--mantine-color-text)",
              // See CustomerLayout.tsx — `undefined` (NOT "transparent") so
              // the .proxvps-nav-item:hover class wins on hover.
              backgroundColor: isActive
                ? "var(--mantine-primary-color-light)"
                : undefined,
              fontWeight: isActive ? 600 : 500,
              fontSize: theme.fontSizes.sm,
            }}
          >
            <Box style={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>
              {item.icon}
            </Box>
            {/* See CustomerLayout.tsx for the label-visibility contract. */}
            {(!collapsed || mobileOpen) && (
              <Text size="sm" fw={isActive ? 600 : 500} style={{ whiteSpace: "nowrap" }}>
                {item.label}
              </Text>
            )}
          </Box>
        )}
      </NavLink>
    );
    if (collapsed && !mobileOpen) {
      return (
        <Tooltip key={item.to} label={item.label} position="right" withArrow>
          {link}
        </Tooltip>
      );
    }
    return <Box key={item.to}>{link}</Box>;
  }

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{
        width: { base: 264, sm: collapsed ? 76 : 248 },
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpen },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" gap="md">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={mobileOpen}
              onClick={toggleMobile}
              size="sm"
              hiddenFrom="sm"
              aria-label="Toggle navigation"
            />
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={() => setCollapsed(!collapsed)}
              visibleFrom="sm"
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {collapsed ? (
                <IconLayoutSidebarLeftExpand size={18} />
              ) : (
                <IconLayoutSidebarLeftCollapse size={18} />
              )}
            </ActionIcon>
            <Text visibleFrom="sm" fw={600} fz="sm" c="dimmed">
              {activeLabel()}
            </Text>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <ThemeToggle />
            <UserMenu session={auth.session!} onLogout={() => void onLogout()} />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <ScrollArea.Autosize mah="100%" key={`nav-${collapsed}-${mobileOpen}`}>
          <Stack gap={0} h="100%">
            <Box px="xs" py="sm">
              {collapsed ? (
                <Box visibleFrom="sm" style={{ display: "flex", justifyContent: "center" }}>
                  <Logo showWordmark={false} size={18} />
                </Box>
              ) : (
                <Logo variant="admin" size={56} />
              )}
            </Box>

            <Box mt="xs" style={{ flex: 1 }}>
              <Stack gap={4}>
                {NAV_SECTIONS.flatMap((section, sectionIdx) => {
                  const children: ReactNode[] = [];
                  if (section.label && (!collapsed || mobileOpen)) {
                    children.push(
                      <Text
                        key={`section-${sectionIdx}-label`}
                        size="xs"
                        fw={600}
                        c="dimmed"
                        tt="uppercase"
                        pl="sm"
                        pt="sm"
                        pb={4}
                        style={{ letterSpacing: "0.06em" }}
                      >
                        {section.label}
                      </Text>
                    );
                  } else if (section.label && collapsed && !mobileOpen) {
                    children.push(<Box key={`section-${sectionIdx}-gap`} h={8} />);
                  }
                  section.items.forEach((item) => children.push(renderNavItem(item)));
                  return children;
                })}
              </Stack>
            </Box>
          </Stack>
        </ScrollArea.Autosize>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}
